/**
 * DOM指标空状态监控系统 - 简化空状态检测器
 */

import type { IDetectionResult, TEmptyStateType } from '../types/simple';
import { SIMPLE_EMPTY_CLASS_NAMES } from '../types/simple';

/**
 * 简化的空状态检测器
 * 提供基础的空状态检测功能
 */
export class SimpleEmptyStateDetector {
  
  /**
   * 检测元素是否为空状态
   */
  isElementEmpty(element: Element): IDetectionResult {
    // 1. 检查文本内容
    const contentResult = this.checkNoContent(element);
    if (contentResult.isEmpty) {
      return contentResult;
    }
    
    // 2. 检查空状态类名
    const classResult = this.checkEmptyClass(element);
    if (classResult.isEmpty) {
      return classResult;
    }
    
    // 3. 检查有效子元素
    // const childrenResult = this.checkNoValidChildren(element);
    // if (childrenResult.isEmpty) {
    //   return childrenResult;
    // }
    
    return { 
      isEmpty: false, 
      emptyType: 'has_content',
      reason: '元素包含有效内容'
    };
  }

  /**
   * 批量检测多个元素
   */
  detectBatch(elements: Element[]): Array<{ element: Element; result: IDetectionResult }> {
    return elements.map(element => ({
      element,
      result: this.isElementEmpty(element)
    }));
  }

  /**
   * 检查元素是否无文本内容
   */
  private checkNoContent(element: Element): IDetectionResult {
    const text = element.textContent?.trim() || '';
    
    // 检查是否为空或常见的空值表示
    if (text === '' || 
        text === '-' || 
        text === '--' || 
        text === 'N/A' || 
        text === 'null' ||
        text === 'undefined' ||
        text === '暂无数据' ||
        text === '无数据') {
      return {
        isEmpty: true,
        emptyType: 'no_content',
        reason: `无有效文本内容: "${text}"`
      };
    }
    
    return { isEmpty: false, emptyType: 'has_content' };
  }

  /**
   * 检查是否包含空状态类名
   */
  private checkEmptyClass(element: Element): IDetectionResult {
    for (const className of SIMPLE_EMPTY_CLASS_NAMES) {
      if (element.classList.contains(className)) {
        return {
          isEmpty: true,
          emptyType: 'empty_class',
          reason: `包含空状态类名: ${className}`
        };
      }
    }
    
    return { isEmpty: false, emptyType: 'has_content' };
  }

  /**
   * 检查是否无有效子元素
   */
  private checkNoValidChildren(element: Element): IDetectionResult {
    const children = Array.from(element.children);
    
    // 过滤掉加载中和占位符元素
    const validChildren = children.filter(child => {
      const classList = child.classList;
      
      // 排除加载状态元素
      if (classList.contains('loading') || 
          classList.contains('skeleton') ||
          classList.contains('placeholder') ||
          classList.contains('spinner')) {
        return false;
      }
      
      // 排除隐藏元素
      const style = window.getComputedStyle(child);
      if (style.display === 'none' || 
          style.visibility === 'hidden' ||
          style.opacity === '0') {
        return false;
      }
      
      return true;
    });
    
    if (validChildren.length === 0) {
      return {
        isEmpty: true,
        emptyType: 'no_children',
        reason: `无有效子元素 (总数: ${children.length}, 有效: 0)`
      };
    }
    
    return { isEmpty: false, emptyType: 'has_content' };
  }

  /**
   * 获取支持的空状态类名
   */
  getSupportedEmptyClassNames(): string[] {
    return [...SIMPLE_EMPTY_CLASS_NAMES];
  }
} 